import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, ButtonGroup, Card, Col, OverlayTrigger, Row, Tooltip, Offcanvas, Spinner, Form } from 'react-bootstrap';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Flex from 'components/common/flex';
import Background from 'components/common/background';
import SubtleBadge from 'components/common/subtleBadge';
import corner4 from 'assets/img/illustrations/corner-4.png';
import usePagination from 'hooks/usePagination';
import useSocket from 'hooks/useSocket';
import { useNavigate } from 'react-router-dom';
import Cookies from "js-cookie";

// -------------------- Action Button --------------------
const ActionButton = ({ tooltip, icon, handleClick, variant = 'tertiary' }) => (
  <OverlayTrigger overlay={<Tooltip style={{ position: 'fixed' }}>{tooltip}</Tooltip>}>
    <Button variant={variant} onClick={handleClick} className="hover-bg-200">
      <FontAwesomeIcon icon={icon} />
    </Button>
  </OverlayTrigger>
);

const NotificationRow = ({ notification, onRowClick, onToggleRead, onDelete, t, isSelected, onSelect }) => {
  const { NotificationId, IsRead, DateTime, Message, RaisedFrom } = notification;

  const IsActionRequired = notification.IsActionRequired ??
    (notification.SourceType === "order" || notification.SourceType === "absence");

  const handleReadToggle = (e) => {
    e.stopPropagation();
    const actionType = IsRead ? "MARK_UNREAD" : "MARK_READ";
    onToggleRead(NotificationId, actionType, !IsRead);

    const message = IsRead ? t("markedUnread") : t("markedRead");
    toast.success(message, { theme: "colored" });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(NotificationId);
  };

  const dateObj = new Date(DateTime);
  const dateOnly = dateObj.toLocaleDateString("en-GB").replace(/\//g, "-");
  const dateTime = dateObj
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(/\//g, "-");

  return (
    <Row
      className={classNames(
        "border-bottom border-200 hover-actions-trigger hover-shadow py-2 px-2 mx-0 align-items-center cursor-pointer position-relative",
        { "bg-body-tertiary": IsRead, "bg-white dark__bg-dark": !IsRead }
      )}
      onClick={() => onRowClick(notification)}
    >
      {/* Checkbox column - Consistent across all screen sizes */}
      <Col xs="auto" className="px-2 d-flex align-items-center">
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(NotificationId, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          className="mb-0 checkbox-sm"
        />
      </Col>

      {/* Desktop hover buttons */}
      <ButtonGroup
        size="sm"
        className="hover-actions end-0 me-10 email-row-actions position-absolute top-50 translate-middle-y d-none d-lg-flex"
        style={{ width: "8rem" }}
      >
        <ActionButton
          tooltip={t("delete") || "Delete"}
          icon="trash"
          handleClick={handleDelete}
        />
        <ActionButton
          tooltip={IsRead ? t("markAsUnread") : t("markAsRead")}
          icon={IsRead ? "envelope" : "envelope-open"}
          handleClick={handleReadToggle}
        />
      </ButtonGroup>

      {/* Notification content - Responsive layout improvements */}
      <Col xs={true} className="pe-0">
        {/* Large devices */}
        <Row className="align-items-center d-none d-lg-flex">
          <Col lg={2}>
            <strong className={classNames({ "fw-bold": !IsRead })}>{RaisedFrom}</strong>
          </Col>
          <Col lg={7} className="text-truncate d-flex align-items-center">
            <span className="text-600 text-truncate">{Message}</span>
            {IsActionRequired && (
              <SubtleBadge bg="warning" className="ms-2">
                {t("actionRequired")}
              </SubtleBadge>
            )}
          </Col>
          <Col lg={3} className="text-end">
            <span className={classNames("fs-10 text-700", { "fw-bold": !IsRead })}>{dateTime}</span>
          </Col>
        </Row>

        {/* Medium devices (tablet) */}
        <Row className="d-none d-md-flex d-lg-none align-items-center">
          <Col md={3}>
            <strong className={classNames({ "fw-bold": !IsRead })}>{RaisedFrom}</strong>
          </Col>
          <Col md={6} className="text-truncate d-flex align-items-center">
            <span className="text-600 text-truncate">{Message}</span>
            {IsActionRequired && (
              <SubtleBadge bg="warning" className="ms-2">
                {t("actionRequired")}
              </SubtleBadge>
            )}
          </Col>
          <Col md={3} className="text-end">
            <span className={classNames("fs-10 text-700", { "fw-bold": !IsRead })}>{dateOnly}</span>
          </Col>
        </Row>

        {/* Mobile devices */}
        <Row className="d-flex d-md-none align-items-center">
          <Col xs={12}>
            <div className="d-flex align-items-start justify-content-between mb-1">
              <div className="d-flex align-items-center flex-grow-1">
                <strong className={classNames("fs-9 text-truncate", { "fw-bold": !IsRead })}>
                  {RaisedFrom}
                </strong>
              </div>
              <span className={classNames("fs-9 text-700 text-nowrap", { "fw-bold": !IsRead })}>{dateOnly}</span>
            </div>

            <div className="d-flex align-items-center justify-content-between">
              <div className="text-truncate flex-grow-1 me-2">
                <span className="text-600 fs-9">{Message.length > 20 ? `${Message.substring(0, 20)}...` : Message}</span>
              </div>

              <ButtonGroup size="sm" onClick={(e) => e.stopPropagation()}>
                <ActionButton
                  tooltip={t("delete") || "Delete"}
                  icon="trash"
                  handleClick={handleDelete}
                />
                <ActionButton
                  tooltip={IsRead ? t("markAsUnread") : t("markAsRead")}
                  icon={IsRead ? "envelope" : "envelope-open"}
                  handleClick={handleReadToggle}
                />
              </ButtonGroup>
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

NotificationRow.propTypes = {
  notification: PropTypes.object.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onToggleRead: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired
};

// -------------------- Notification Footer --------------------
const NotificationFooter = ({
  totalItems,
  from,
  to,
  canNextPage,
  canPreviousPage,
  nextPage,
  prevPage,
  setRowsPerPage,
  rowsPerPage,
  t
}) => {
  const [perPage, setPerPage] = useState(rowsPerPage);
  const rowsPerPageOptions = [25, 50, 75, 100];

  return (
    <Flex
      className={classNames(
        "align-items-center justify-content-between  p-3"
      )}
    >
      <Flex alignItems="center" className="fs-10">
        <p className="mb-0 d-none d-md-block">
          {from} {t("toLable")} {to} {t("of")} {totalItems}
        </p>

        <p className="mb-0 mx-2">{t("rowsPerPage")}:</p>
        <Form.Select
          size="sm"
          className="w-auto"
          value={perPage}
          onChange={(e) => {
            const value = Number(e.target.value);
            setPerPage(value);
            setRowsPerPage(value);
            Cookies.set("rowsPerPage_notifications", value);
          }}
        >
          {rowsPerPageOptions.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Form.Select>
      </Flex>

      <Flex
        alignItems="center"
        justifyContent="center"
        className="flex-wrap gap-2 pagination-controls"
      >
        <Button
          size="sm"
          variant={canPreviousPage ? "primary" : "tertiary"}
          onClick={prevPage}
          className={classNames({ disabled: !canPreviousPage })}
        >
          {t("previous")}
        </Button>
        <Button
          size="sm"
          variant={canNextPage ? "primary" : "tertiary"}
          onClick={nextPage}
          className={classNames("px-4 ms-2", {
            disabled: !canNextPage,
          })}
        >
          {t("nextLable")}
        </Button>
      </Flex>
    </Flex>
  );
};

// -------------------- Notifications Element --------------------
const NotificationsElement = ({ t }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const userId = localStorage.getItem("UserId");
  const customerId = localStorage.getItem("customerId");
  const navigate = useNavigate();

  const fetchUsers = async (customerId) => {
    if (!customerId) return [];
    try {
      const userResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/users/allLocations`,
        {
          params: { customerId: customerId },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      return userResponse.data;
    } catch (error) {
      console.error("Error fetching User data:", error);
      return [];
    }
  };

  const getUserNameById = (userId) => {
    if (!userData || userData.length === 0) return userId;
    const user = userData.find((u) =>
      String(u.UserNumber) === String(userId) || String(u.UserId) === String(userId)
    );
    return user ? user.Name : userId;
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const [users, notificationsRes] = await Promise.all([
        fetchUsers(customerId),
        axios.get(
          `${process.env.REACT_APP_API_URL}/notificationsRoute/notifications/user/${userId}`,
          {
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
          }
        )
      ]);

      setUserData(users);

      // Handle null or empty API response
      const data = notificationsRes.data;
      if (!data || data.length === 0) {
        setNotifications([]); // set empty array instead of null
      } else {
        setNotifications(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateNotificationReadStatus = async (notificationId, isReadStatus) => {
    setNotifications(prev =>
      prev.map(n =>
        n.NotificationId === notificationId ? { ...n, IsRead: isReadStatus } : n
      )
    );

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/notificationsRoute/notifications/${notificationId}`,
        { isRead: isReadStatus },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
    } catch (err) {
      console.error("Failed to patch notification:", err);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/notificationsRoute/notifications/${notificationId}`,
        {
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
        }
      );

      // Remove notification from state
      setNotifications(prev => prev.filter(n => n.NotificationId !== notificationId));

      // Remove from selected notifications if present
      setSelectedNotifications(prev => {
        const newSelected = new Set(prev);
        newSelected.delete(notificationId);
        return newSelected;
      });

      toast.success(t("notification.deleteSuccess"), { theme: "colored" });
    } catch (err) {
      toast.error(t("notification.deleteFailed"), { theme: "colored" });
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Create array of delete promises
      const deletePromises = Array.from(selectedNotifications).map(notificationId =>
        axios.delete(
          `${process.env.REACT_APP_API_URL}/notificationsRoute/notifications/${notificationId}`,
          {
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
          }
        )
      );

      // Wait for all delete operations to complete
      await Promise.all(deletePromises);

      // Remove deleted notifications from state
      setNotifications(prev =>
        prev.filter(n => !selectedNotifications.has(n.NotificationId))
      );

      toast.success(`${selectedNotifications.size} ${t("notification.deleteMultipleSuccess")}`, { theme: "colored" });
      setSelectedNotifications(new Set());
    } catch (err) {
      console.error("Failed to delete notifications:", err);
      toast.error(t("notification.deleteSomeFailed"), { theme: "colored" });
    }
  };

  const handleToggleRead = (notificationId, actionType, isReadValue) => {
    updateNotificationReadStatus(notificationId, isReadValue);
  };

  const handleBulkToggleRead = (markAsRead) => {
    selectedNotifications.forEach(id => {
      handleToggleRead(id, markAsRead ? 'MARK_READ' : 'MARK_UNREAD', markAsRead);
    });

    const message = markAsRead ? t("markedRead") : t("markedUnread");
    toast.success(
      `${selectedNotifications.size} notifications ${markAsRead
        ? t("notification.markedAsRead")
        : t("notification.markedAsUnread")}`,
      { theme: "colored" }
    );
    setSelectedNotifications(new Set());
  };

  const handleSelectNotification = (notificationId, isSelected) => {
    setSelectedNotifications(prev => {
      const newSelected = new Set(prev);
      if (isSelected) {
        newSelected.add(notificationId);
      } else {
        newSelected.delete(notificationId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (isSelected, currentPageNotifications) => {
    if (isSelected) {
      const allIds = new Set(currentPageNotifications.map(n => n.NotificationId));
      setSelectedNotifications(allIds);
    } else {
      setSelectedNotifications(new Set());
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  useSocket('notification:new', (payload) => {
    if (!payload?.NotificationId) return;
    const { unreadCount, ...row } = payload;
    setNotifications(prev =>
      prev.some(n => n.NotificationId === row.NotificationId) ? prev : [row, ...prev]
    );
  });

  const [rowsPerPage, setRowsPerPage] = useState(
    Number(Cookies.get("rowsPerPage_notifications")) || 50
  );

  const {
    paginationState: {
      data: paginatedNotifications,
      totalItems,
      canNextPage,
      canPreviousPage,
      from,
      to
    },
    nextPage,
    prevPage,
    setItemsPerPage
  } = usePagination(notifications, rowsPerPage);

  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);

  const handleRowClick = notification => {
    const isActionRequired = notification.IsActionRequired ??
      (notification.SourceType === "order" || notification.SourceType === "absence");

    const notificationWithName = {
      ...notification,
      RaisedFrom: getUserNameById(notification.RaisedFrom),
      IsActionRequired: isActionRequired
    };
    setSelectedNotification(notificationWithName);

    if (!notification.IsRead) {
      handleToggleRead(notification.NotificationId, 'MARK_READ', true);
    }
    setShowDrawer(true);
  };

  const getNotificationRoute = (notification) => {
    const { SourceType, SourceId, SourceDate } = notification;
    if (!SourceType) return null;

    switch (SourceType.toLowerCase()) {
      case "task":
        return {
          path: "/planning",
          state: { openTaskId: SourceId, taskDate: SourceDate }
        };
    case "complaint":
        return {
          path: "/planning",
          state: { openTaskId: SourceId, openComplaint: true, taskDate: SourceDate }
        };
      case "order": {
        const isMedewerker = localStorage.getItem("isMedewerker") === "true";
        const isEmployeeView = sessionStorage.getItem("isEmployeeView") === "true";
        const isEmployee = isMedewerker || isEmployeeView;
        return {
          path: "/planning",
          state: { openTaskOrderId: SourceId, taskDate: SourceDate }
        };
      }
      case "orderversionupdate":
        return {
          path: `/orders/${SourceId}/details-order`,
          state: null
        };
      case "absence":
        return {
          path: `/leave-management?unavailabilityId=${SourceId}`,
          state: null
        };
      case "user":
        return { path: "/profile", state: null };
      default:
        return null;
    }
  };

  if (loading) return (
    <div className="text-center my-5 p-5">
      <Spinner animation="border" role="status">
        <span className="visually-hidden">{t("loading")}</span>
      </Spinner>
    </div>
  );

  const isAllSelected = paginatedNotifications.length > 0 &&
    selectedNotifications.size === paginatedNotifications.length;

  // Determine which bulk read button to show
  const getBulkReadButton = () => {
    if (selectedNotifications.size === 0) return null;

    // Check if any selected notification is unread
    const hasUnread = Array.from(selectedNotifications).some(id => {
      const notification = notifications.find(n => n.NotificationId === id);
      return !notification?.IsRead;
    });

    // If any notification is unread, show "Mark as Read"
    // If all are read, show "Mark as Unread"
    if (hasUnread) {
      return (
        <ActionButton
          tooltip={t("markAsRead") || "Mark as Read"}
          icon="envelope-open"
          handleClick={() => handleBulkToggleRead(true)}
        />
      );
    } else {
      return (
        <ActionButton
          tooltip={t("markAsUnread") || "Mark as Unread"}
          icon="envelope"
          handleClick={() => handleBulkToggleRead(false)}
        />
      );
    }
  };

  return (
    Array.isArray(paginatedNotifications) && paginatedNotifications.length > 0 ? (
      <Card>
        <Card.Header className="border-bottom border-200 p-3">
          <Flex alignItems="center" className="gap-2 flex-row justify-content-between flex-wrap">
            <Flex alignItems="center" className="gap-2 flex-column flex-sm-row text-center text-sm-start">
              <Form.Check
                type="checkbox"
                id="select-all-notifications"
                label={t("selectAllNotifications") || "Select all notifications"}
                checked={isAllSelected}
                onChange={(e) => handleSelectAll(e.target.checked, paginatedNotifications)}
                className="mb-0 checkbox-sm"
              />
            </Flex>

            {selectedNotifications.size > 0 && (
              <ButtonGroup size="sm" className="flex-nowrap mt-2 mt-sm-0">
                <ActionButton
                  tooltip={t("delete") || "Delete"}
                  icon="trash"
                  handleClick={handleBulkDelete}
                />
                {/* Show only one read/unread button based on selection state */}
                {getBulkReadButton()}
              </ButtonGroup>
            )}
          </Flex>
        </Card.Header>

        <Card.Body className="fs-10 border-top border-200 p-0">
          {paginatedNotifications.length > 0 ? (
            paginatedNotifications.map(notification => (
              <NotificationRow
                key={notification.NotificationId}
                notification={{
                  ...notification,
                  RaisedFrom: getUserNameById(notification.RaisedFrom)
                }}
                onRowClick={handleRowClick}
                onToggleRead={handleToggleRead}
                onDelete={handleDeleteNotification}
                isSelected={selectedNotifications.has(notification.NotificationId)}
                onSelect={handleSelectNotification}
                t={t}
              />
            ))
          ) : (
            <Alert variant="info" className="mb-0 rounded-0">
              <h5 className="alert-heading">{t("noNotifications")}</h5>
              <hr />
            </Alert>
          )}
        </Card.Body>

        <NotificationFooter
          totalItems={totalItems}
          from={from}
          to={to}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          nextPage={nextPage}
          prevPage={prevPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={(value) => {
            setRowsPerPage(value);
            setItemsPerPage(value);
          }}
          t={t}
        />

        {/* Drawer */}
        <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="end" className="task-offcanvas">
          <Offcanvas.Header className="px-4 py-3 border-bottom shadow-sm" closeButton>
            <div className="w-100">
              {selectedNotification && (
                <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1 gap-sm-2">
                  <h4 className="mb-0">{selectedNotification.RaisedFrom}</h4>
                  {selectedNotification.IsActionRequired ? (
                    <SubtleBadge bg="warning">
                      {t("actionRequired")}
                    </SubtleBadge>
                  ) : (
                    <SubtleBadge bg="success">
                      {t("noactionRequired")}
                    </SubtleBadge>
                  )}
                </div>
              )}
            </div>
          </Offcanvas.Header>

          <Offcanvas.Body className="px-4 py-4 thin-scrollbar">
            {selectedNotification && (
              <>
                <div className="d-flex flex-column gap-3 fs-9 mb-4">
                  <div className="d-flex align-items-start">
                    <div className="d-flex align-items-center  me-3">
                      <FontAwesomeIcon icon="clock" className="me-2 icon-opacity" />
                      <strong className="data-font-size fw-bold"> {t("datetime")}</strong>
                    </div>
                    <div className="text-end flex-grow-1 data-font-size data-font-colour">
                      {new Date(selectedNotification.DateTime).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false
                      }).replace(/\//g, "-")}
                    </div>
                  </div>

                  <div className="d-flex align-items-start">
                    <div className="d-flex align-items-center me-3">
                      <FontAwesomeIcon icon="user" className="me-2 icon-opacity" />
                      <strong className="data-font-size fw-bold">  {t("raisedFrom")}</strong>
                    </div>
                    <div className="text-end flex-grow-1 data-font-size data-font-colour">{selectedNotification.RaisedFrom}</div>
                  </div>

                  <div className="mb-2">
                    <div className="d-flex align-items-center mb-2">
                      <FontAwesomeIcon icon="align-left" className="me-2 icon-opacity" />
                      <strong className="data-font-size fw-bold">{t("message")}</strong>
                    </div>
                    <div className="data-font-size data-font-colour bg-100 rounded p-3" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedNotification.Message}
                    </div>
                  </div>
                </div>

                <div className="mt-5 text-center">
                  {(() => {
                    const route = getNotificationRoute(selectedNotification);
                    return route ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          if (route.state) {
                            sessionStorage.setItem("pendingNavState", JSON.stringify(route.state));
                          }
                          navigate(route.path, { state: route.state });
                          setTimeout(() => setShowDrawer(false), 100);
                        }}
                      >
                        <FontAwesomeIcon icon="external-link-alt" className="me-1" />
                        {t("goToSource")}
                      </Button>
                    ) : (
                      <Button variant="outline-secondary" size="sm" disabled>
                        <FontAwesomeIcon icon="external-link-alt" className="me-1" />
                        {t("goToSource")}
                      </Button>
                    );
                  })()}
                </div>
              </>
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </Card>
    ) : (
      <Card className="text-center py-5">
        <Card.Body>
          <div className="text-center p-4">{t("noNotifications")}</div>
        </Card.Body>
      </Card>
    )
  );
};

// -------------------- Main Notifications --------------------
function Notifications({ userId }) {
  const { t } = useTranslation();

  return (
    <Row className="g-3 mb-3">
      <Col lg={12} xxl={12}>
        <Card className="mb-3 h-100">
          <Background
            image={corner4}
            className="bg-card"
            style={{ borderTopRightRadius: '0.375rem', borderBottomRightRadius: '0.375rem' }}
          />
          <Card.Body className="position-relative pe-4 pb-2">
            <Row className="flex-between-center">
              <Col md>
                <h5 className="mb-2 mb-md-0">{t('notifications')}</h5>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>

      <Col lg={12} xxl={12}>
        <NotificationsElement userId={userId} t={t} />
      </Col>
    </Row>
  );
}

export default Notifications;